import { useState, useCallback, useEffect } from 'react';
import { apiService } from '../../../shared/services/api';
import { ApiRoutes } from '../../../shared/config/constants';
import { ClientInfo } from '../types';

interface UseClientInfoResult {
  client: ClientInfo | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useClientInfo(clientId: number | null): UseClientInfoResult {
  const [client, setClient] = useState<ClientInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchClient = useCallback(async () => {
    if (!clientId) {
      setClient(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.get<ClientInfo>(
        ApiRoutes.Clients.Info(clientId)
      );

      if (response.success && response.data) {
        setClient(response.data);
      } else {
        setError('Client not found');
        setClient(null);
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            'Failed to load client';
      setError(errorMessage);
      setClient(null);
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  return { client, isLoading, error, refetch: fetchClient };
}
